import { Button, Spin } from "antd";
import { DocumentTextOutline, ChevronBackOutline } from "@raresail/react-ionicons";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import useGet from "../../../hooks/common/useGet";
import { downloadPdf } from "../../../utils/pdfDownload";
import { Heading } from "../../elements/Elements";
import { PRIMARY_COLOR, primary5 } from "../../../constants/colors";

type Section = { heading: string; paragraphs: string[] };
type TosPayload = {
  title: string;
  version: string;
  effective_date: string;
  intro: string;
  sections: Section[];
};

/**
 * Public Terms of Service page. Renders the structured payload from
 * /api/legal/terms-of-service/ and offers a PDF download.
 */
export const TermsOfServicePage = () => {
  const { loadData, data, loading } = useGet();
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadData({ api: "legal/terms-of-service/" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadPdf(
        "legal/terms-of-service/?format=pdf",
        "everpesa_terms_of_service.pdf",
        { open: true },
      );
    } finally {
      setDownloading(false);
    }
  };

  const payload: TosPayload | null = data?.title ? data : null;

  return (
    <Page>
      <TopBar>
        <Button
          type="link"
          icon={<ChevronBackOutline />}
          onClick={() => navigate(-1)}
          style={{ padding: 0 }}
        >
          Back
        </Button>
        <Button
          type="primary"
          icon={<DocumentTextOutline />}
          onClick={handleDownload}
          loading={downloading}
          style={{ background: PRIMARY_COLOR }}
        >
          Download PDF
        </Button>
      </TopBar>

      <Content className="r10 whiteBg">
        {loading || !payload ? (
          <div className="flexCenter" style={{ minHeight: 240 }}>
            <Spin />
          </div>
        ) : (
          <>
            <Heading className="font20">{payload.title}</Heading>
            <div className="font11 grey mb15">
              Version {payload.version} · Effective {formatDate(payload.effective_date)}
            </div>
            <p className="font13" style={{ lineHeight: 1.6 }}>{payload.intro}</p>

            {payload.sections.map((s) => (
              <section key={s.heading} className="mt20">
                <h3 className="font16 semiBold" style={{ color: PRIMARY_COLOR, margin: "0 0 6px 0" }}>
                  {s.heading}
                </h3>
                {s.paragraphs.map((p, i) => (
                  <p key={i} className="font13" style={{ lineHeight: 1.6 }}>
                    {p}
                  </p>
                ))}
              </section>
            ))}

            <Footer>
              Everpesa Technologies Ltd · Plot 14, Kampala, Uganda · legal@everpesa.com
            </Footer>
          </>
        )}
      </Content>
    </Page>
  );
};

const formatDate = (iso?: string) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const Page = styled.div`
  padding: 16px;
  max-width: 880px;
  margin: 0 auto;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const Content = styled.div`
  padding: 28px 28px 36px;
  border: 1px solid ${primary5};
  @media (max-width: 600px) {
    padding: 18px 16px 28px;
  }
`;

const Footer = styled.div`
  margin-top: 32px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  font-size: 11px;
  color: #64748b;
  text-align: center;
`;
